import { Link } from "react-router-dom";
import { ArrowRight, Home, Key, Camera, CalendarCheck, Sparkles, Star, Clock, Shield, Phone, CheckCircle2, TrendingUp, Users } from "lucide-react";
import Reveal from "@/components/Reveal";
import AnimatedCounter from "@/components/AnimatedCounter";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import SEO, { LocalBusinessSchema, ServiceSchema, FAQSchema } from "@/components/SEO";
import PartnersMarquee from "@/components/PartnersMarquee";

const CITIES = [
  { name: "Aigues-Mortes", path: "/conciergerie-aigues-mortes", img: "/images/aigues-mortes.webp" },
  { name: "La Grande-Motte", path: "/conciergerie-la-grande-motte", img: "/images/grande-motte-4.jpg" },
  { name: "Carnon", path: "/conciergerie-carnon", img: "/images/carnon-3.jpg" },
  { name: "Le Grau-du-Roi", path: "/conciergerie-grau-du-roi", img: "/images/grau-du-roi-5.png" },
];

export default function HomePage() {
  return (
    <div>
      <SEO
        title="Conciergerie premium La Grande-Motte, Aigues-Mortes, Grau-du-Roi"
        description="Golden Sea Conciergerie : gestion locative saisonnière premium à La Grande-Motte, Aigues-Mortes, Le Grau-du-Roi et Carnon. Conciergerie Airbnb, sous-location, accueil voyageurs. Devis gratuit."
        path="/"
      />
      <LocalBusinessSchema />
      <ServiceSchema />
      <FAQSchema faqs={[
        { question: "Qu'est-ce qu'une conciergerie locative ?", answer: "Une conciergerie locative gère votre bien en location saisonnière : création d'annonces, accueil voyageurs, ménage, communication, tarification dynamique. Chez Golden Sea, nous prenons tout en charge pour vous." },
        { question: "Combien coûte le service de conciergerie Golden Sea ?", answer: "Notre commission est de 23% du chiffre d'affaires généré par la location. Le devis est gratuit et sans engagement." },
        { question: "Quelles zones couvrez-vous ?", answer: "Nous intervenons à La Grande-Motte, Aigues-Mortes, Le Grau-du-Roi et Carnon-Plage, sur le littoral méditerranéen du Gard et de l'Hérault." },
        { question: "Proposez-vous la sous-location ?", answer: "Oui, nous proposons un service de sous-location professionnelle avec un loyer garanti chaque mois, 12 mois sur 12, sans aucune gestion de votre part." },
        { question: "Quel est le délai de réponse pour un devis ?", answer: "Nous nous engageons à vous recontacter sous 48h maximum avec une proposition personnalisée et gratuite." },
      ]} />
      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 bg-[#111]">
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="/images/poster-grande-motte-drone-hero.jpg"
            className="absolute inset-0 w-full h-full object-cover opacity-70"
          >
            <source src="/images/grande-motte-drone-hero.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0" style={{background: 'linear-gradient(to right, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.1) 100%)'}} />
        </div>
        <div className="relative z-10 w-full section-container pt-28 pb-16">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left content */}
            <div>
              <Reveal>
                <div className="inline-flex items-center gap-2 bg-[#d4af37]/20 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-[#d4af37]/30">
                  <Star className="w-4 h-4 text-[#d4af37]" />
                  <span className="text-white text-sm font-medium">Note Google 5/5 — 14 avis</span>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <h1 className="text-white mb-6" style={{fontFamily: 'var(--font-display)', lineHeight: 1.1, fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', fontWeight: 700}}>
                  Votre conciergerie de confiance,{' '}<br className="hidden sm:block" />
                  pour vos biens <span className="text-[#d4af37]">en bord de mer</span>
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-white/85 text-base sm:text-lg leading-relaxed mb-8 max-w-xl" style={{fontFamily: 'var(--font-sans)'}}>
                  Conciergerie pour propriétaires à La Grande-Motte, Aigues-Mortes, Carnon et Le Grau-du-Roi. Gestion complète, ménage hôtelier, organisation des tarifs.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <Link to="/devis" className="inline-flex items-center gap-2 bg-white text-[#111] rounded-xl px-7 py-3.5 font-semibold text-sm hover:bg-white/90 transition-all duration-300 shadow-[0_4px_15px_rgba(0,0,0,0.15)]" style={{fontFamily: 'var(--font-sans)'}}>
                    Devis gratuit <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href="tel:0615166415" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white border border-white/30 rounded-xl px-7 py-3.5 font-semibold text-sm hover:bg-white/20 transition-all duration-300" style={{fontFamily: 'var(--font-sans)'}}>
                    <Phone className="w-4 h-4" /> Nous appeler
                  </a>
                </div>
              </Reveal>
              <Reveal delay={0.4}>
                <p className="text-white/40 text-xs" style={{fontFamily: 'var(--font-sans)'}}>
                  Réponse garantie sous 48h — Devis gratuit et sans engagement
                </p>
              </Reveal>
            </div>

            {/* Right card - Pourquoi Golden Sea ? */}
            <Reveal delay={0.3}>
              <div className="hidden lg:block">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/15 p-8 shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
                  <h3 className="text-white text-xl font-semibold mb-6" style={{fontFamily: 'var(--font-display)'}}>
                    Pourquoi Golden Sea ?
                  </h3>
                  <div className="space-y-3">
                    {[
                      "Gestion complète de A à Z",
                      "Commission unique 23% TTC",
                      "Aucun engagement",
                      "Disponible 7j/7",
                      "Tarification dynamique",
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#d4af37] flex-shrink-0" />
                        <span className="text-white text-sm font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
        {/* Bannière partenaires intégrée au hero */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <PartnersMarquee variant="hero" />
        </div>
      </section>

      {/* Transition : 3 piliers animés */}
      <section className="py-12 sm:py-16 bg-white overflow-hidden">
        <div className="section-container">
          <Reveal>
            <div className="flex items-center justify-center gap-3 sm:gap-5 mb-10 sm:mb-14">
              {[
                { icon: Home, label: "Gestion" },
                { icon: Key, label: "Location" },
                { icon: Sparkles, label: "Excellence" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 sm:gap-5">
                  {i > 0 && <div className="w-8 sm:w-14 h-px bg-gradient-to-r from-[#d4af37]/20 via-[#d4af37] to-[#d4af37]/20" />}
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#faf6eb] border border-[#f1eee6] flex items-center justify-center shadow-[0_4px_16px_rgba(212,175,55,0.08)] hover:scale-110 hover:shadow-[0_8px_24px_rgba(212,175,55,0.15)] transition-all duration-300">
                      <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4af37]" />
                    </div>
                    <span className="text-[10px] sm:text-xs font-medium text-[#999] tracking-wide uppercase" style={{fontFamily: 'var(--font-sans)'}}>{item.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="max-w-4xl mx-auto">
            <Reveal delay={0.1}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111] mb-8 text-center" style={{fontFamily: 'var(--font-display)', lineHeight: 1.15}}>
                Confiez la gestion de votre bien à <span className="text-[#d4af37]">GOLDEN SEA</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-[#555] leading-relaxed text-base sm:text-lg mb-10 text-center max-w-3xl mx-auto">
                Nous accompagnons les propriétaires de résidences secondaires dans la location de courte durée à la Grande-Motte, Aigues-Mortes, Carnon et le Grau-du-Roi. Notre service de conciergerie sur-mesure vous libère de toutes les contraintes liées à la location de votre logement : annonce, ménage, accueil et gestion des voyageurs... Notre mission ? Maximiser vos revenus locatifs, valoriser votre bien et vous offrir une tranquillité d'esprit totale.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Séparateur moderne */}
      <div className="relative bg-white">
        <div className="flex items-center justify-center py-2">
          {/* Ligne gauche */}
          <div className="h-px flex-1 max-w-[160px] sm:max-w-[240px] bg-gradient-to-r from-transparent via-[#e8e4d9] to-[#d4af37]/40" />
          {/* Losange doré central */}
          <div className="relative mx-4 sm:mx-6">
            <div className="w-3 h-3 rotate-45 bg-gradient-to-br from-[#d4af37] to-[#c19b2e] shadow-[0_0_12px_rgba(212,175,55,0.3)]" />
          </div>
          {/* Ligne droite */}
          <div className="h-px flex-1 max-w-[160px] sm:max-w-[240px] bg-gradient-to-l from-transparent via-[#e8e4d9] to-[#d4af37]/40" />
        </div>
      </div>

      {/* Section: 2 Services */}
      <section className="py-16 sm:py-20 bg-[#fbfaf7]">
        <div className="section-container">
          <div className="grid gap-8 md:grid-cols-2">
            <Reveal>
              <div className="group bg-white rounded-2xl border border-[#e8e4d9] p-8 sm:p-10 shadow-[0_12px_40px_rgba(10,10,20,0.06)] hover:shadow-[0_16px_60px_rgba(10,10,20,0.10)] transition-all duration-300 h-full flex flex-col">
                <div className="w-14 h-14 bg-[#f8f3e3] rounded-xl flex items-center justify-center mb-6 group-hover:rotate-[-6deg] transition-transform duration-300">
                  <Home className="w-7 h-7 text-[#d4af37]" />
                </div>
                <h3 className="text-2xl font-semibold text-[#111] mb-4" style={{fontFamily: 'var(--font-display)'}}>
                  Conciergerie location courte durée
                </h3>
                <p className="text-[#555] leading-relaxed mb-8 flex-1">
                  Confiez-nous la gestion complète de vos locations saisonnières. Golden Sea assure l'intendance de votre bien : on s'occupe de tout, pour que vous n'ayez rien à gérer. Rentabilité, tranquillité et service de qualité au rendez-vous.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/devis" className="btn-gold inline-flex items-center gap-2" data-testid="services-conciergerie-devis-button">
                    Devis gratuit <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/conciergerie" className="btn-gold-outline inline-flex items-center gap-2" data-testid="services-conciergerie-button">
                    En savoir plus
                  </Link>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="group bg-white rounded-2xl border border-[#e8e4d9] p-8 sm:p-10 shadow-[0_12px_40px_rgba(10,10,20,0.06)] hover:shadow-[0_16px_60px_rgba(10,10,20,0.10)] transition-all duration-300 h-full flex flex-col">
                <div className="w-14 h-14 bg-[#f8f3e3] rounded-xl flex items-center justify-center mb-6 group-hover:rotate-[-6deg] transition-transform duration-300">
                  <Key className="w-7 h-7 text-[#d4af37]" />
                </div>
                <h3 className="text-2xl font-semibold text-[#111] mb-4" style={{fontFamily: 'var(--font-display)'}}>
                  Sous-location meublée
                </h3>
                <p className="text-[#555] leading-relaxed mb-8 flex-1">
                  Avec notre service de sous-location, nous devenons locataire de votre logement et vous percevez un loyer fixe garanti chaque mois. Finis les imprévus et la vacance locative. Une solution simple, rentable et légale.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/devis" className="btn-gold inline-flex items-center gap-2" data-testid="services-souslocation-devis-button">
                    Devis gratuit <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/sous-location-conciergerie" className="btn-gold-outline inline-flex items-center gap-2" data-testid="services-souslocation-button">
                    En savoir plus
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Section: 4 Villes */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="section-container">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CITIES.map((city, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <Link to={city.path} data-testid="city-card" className="group block overflow-hidden rounded-2xl border border-[#f1eee6] bg-white shadow-[0_12px_40px_rgba(10,10,20,0.06)] hover:shadow-[0_16px_60px_rgba(10,10,20,0.10)] transition-shadow duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={city.img} alt={city.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-[#111] mb-2" style={{fontFamily: 'var(--font-display)'}}>{city.name}</h3>
                    <span className="text-sm text-[#d4af37] font-medium flex items-center gap-1 group-hover:gap-2 transition-all duration-200">En savoir plus <ArrowRight className="w-3.5 h-3.5" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Section: Chiffres clés */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="section-container">
          <Reveal>
            <div className="text-center mb-16 sm:mb-20">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111]" style={{fontFamily: 'var(--font-display)', lineHeight: 1.15}}>
                Golden Sea <span className="text-[#d4af37]">en chiffres</span>
              </h2>
              <div className="w-12 h-0.5 bg-[#d4af37] mx-auto mt-5 rounded-full" />
            </div>
          </Reveal>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8 max-w-5xl mx-auto">
            {[
              { end: 4, suffix: "", icon: CalendarCheck, label: "Années d'expérience", delay: 0 },
              { end: 50, suffix: "+", icon: Users, label: "Voyageurs accueillis", delay: 0.12 },
              { end: 7, suffix: "j/7", icon: Clock, label: "Disponibilité totale", delay: 0.24 },
              { end: 100, suffix: "%", icon: Star, label: "Clients satisfaits", delay: 0.36 },
            ].map((stat, i) => (
              <Reveal key={i} delay={stat.delay}>
                <div className="group text-center py-6 sm:py-8">
                  {/* Icon */}
                  <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-5 rounded-full bg-[#faf6eb] border border-[#f1eee6] flex items-center justify-center group-hover:border-[#d4af37]/30 group-hover:shadow-[0_8px_24px_rgba(212,175,55,0.1)] group-hover:scale-105 transition-all duration-400">
                    <stat.icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#d4af37]" />
                  </div>
                  {/* Number */}
                  <AnimatedCounter end={stat.end} suffix={stat.suffix} />
                  {/* Label */}
                  <p className="text-[#888] text-xs sm:text-sm font-medium mt-1" style={{fontFamily: 'var(--font-sans)'}}>
                    {stat.label}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Section: Voyageurs */}
      <section className="py-16 sm:py-20 bg-[#fbfaf7]">
        <div className="section-container">
          <Reveal>
            <div className="bg-gradient-to-br from-[#fbfaf7] to-[#f1eee6] rounded-2xl border border-[#e8e4d9] p-10 sm:p-14 text-center relative overflow-hidden shadow-[0_12px_40px_rgba(10,10,20,0.06)]">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#d4af37] via-[#e8c84a] to-[#d4af37]" />
              <div className="relative z-10">
                <h2 className="text-3xl sm:text-4xl font-semibold text-[#111] mb-4" style={{fontFamily: 'var(--font-display)'}}>
                  Vous êtes voyageur ?
                </h2>
                <p className="text-[#555] mb-8 max-w-lg mx-auto">
                  En vacances ou de passage dans notre belle région, vous êtes à la recherche d'un hébergement ?
                </p>
                <Link to="/nos-locations" className="btn-gold inline-flex items-center gap-2">
                  Découvrez nos locations <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Section: Avis Google */}
      <ReviewsCarousel />

    </div>
  );
}
